import { useContext, useEffect, useState } from "react";
import {
  Row,
  Col,
  Button,
  Skeleton,
  Result,
  Empty,
} from "antd";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import { PlusOutlined, CloudUploadOutlined } from "@ant-design/icons";
import ServiceTable from "./ServiceTable";
import {
  getFacilityServicesApi,
  getServicesApi,
  uploadServicesApi,
} from "../../../../services/service.services";
import BulkUploadModal from "../../../common/BulkUploadModal";
import { prepareServiceBulkUploadFile } from "../../../../helpers/bulkUploadExcel";
import { UserContext } from "../../../../context/UserContext";
import { useNavigate } from "react-router-dom";
import useQuery from "../../../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../../../helpers/urlFilters";
import { FacilityID, Role } from "../../../../constants/defaultKeys";
import { getCookie } from "../../../../helpers/localStorage";
import ServiceForm from "./ServiceForm";
import useIsMobile from "../../../../hooks/useIsMobile";
import { UiDataTable } from "../../../ui";

const Service = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { userDetails } = useContext(UserContext);
  const params = useQuery();
  const [isVisible, setIsVisible] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [data, setData] = useState({ data: [] });
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    () => parseSearchParam(params) ?? ""
  );
  const [page, setPage] = useState(() => parsePageParam(params));

  const defaultRole = userDetails?.currRole || getCookie(Role);
  const facilityId =
    Number(getCookie(FacilityID)) || userDetails?.currFacility;

  const refreshPage = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    setLoading(true);

    const queryParams = {
      page: searchQuery ? 1 : page,
      limit: 100,
    };

    if (searchQuery) {
      queryParams.search = searchQuery;
    }

    if (defaultRole !== "Facility_Admin") {
      queryParams.facility = facilityId;
    }

    const queryString = new URLSearchParams(queryParams);
    const request =
      defaultRole === "Facility_Admin"
        ? getFacilityServicesApi(facilityId, queryString)
        : getServicesApi(queryString);

    request
      .then((res) => {
        setData(res?.data ?? { data: [] });
      })
      .catch((err) => {
        console.log(err);
        setData({ data: [] });
      })
      .finally(() => {
        setLoading(false);
        setInitialLoad(false);
      });
  }, [
    refresh,
    searchQuery,
    page,
    defaultRole,
    facilityId,
    params?.facility,
    userDetails,
  ]);

  const onServiceSearch = (e) => {
    const query = e?.target?.value ?? "";
    setSearchQuery(query);
    setPage(1);
    updateUrlSearchParams(navigate, { search: query || null, page: null });
  };

  const handlePageChange = (value) => {
    setPage(value);
    updateUrlSearchParams(navigate, { page: value === 1 ? null : value });
  };

  const showSearch = data?.data?.length > 0 || Boolean(searchQuery);
  const isOrgAdmin =
    defaultRole !== "Facility_Admin" && defaultRole !== "Receptionist";
  const showAddBtn = isOrgAdmin && data?.data?.length > 0;
  const showTable = data?.data?.length > 0 || Boolean(searchQuery);

  if (initialLoad && loading) {
    return <Skeleton active />;
  }

  return (
    <>
      {showTable ? (
        <UiDataTable
          showHeader={false}
          shellClassName="ui-table-fill-shell"
          onSearch={onServiceSearch}
          searchPlaceholder="Search services"
          showSearch={showSearch}
          toolbarTrailing={
            isOrgAdmin ? (
              <div style={{ display: "flex", gap: 12 }}>
                <Button
                  className={btnStyles.lgBtn}
                  icon={<CloudUploadOutlined />}
                  onClick={() => setUploadModalOpen(true)}
                >
                  Upload Excel
                </Button>
                {showAddBtn && (
                  <Button
                    className={btnStyles.lgBtn}
                    type="primary"
                    onClick={() => setIsVisible(true)}
                  >
                    <PlusOutlined />
                    Service
                  </Button>
                )}
              </div>
            ) : null
          }
        >
          <ServiceTable
            data={data?.data}
            refreshPage={refreshPage}
            userDetails={userDetails}
            loading={loading}
            page={page}
            setPage={handlePageChange}
            totalCount={data?.total_count}
          />
        </UiDataTable>
      ) : (
        <>
          {defaultRole !== "Facility_Admin" ? (
            <Row style={{ display: "flex", justifyContent: "center" }}>
              <Col
                span={isMobile ? 24 : 8}
                style={{
                  textAlign: "center",
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                {isOrgAdmin && (
                  <Button
                    icon={<CloudUploadOutlined />}
                    onClick={() => setUploadModalOpen(true)}
                    className={btnStyles.lgBtn}
                    block={isMobile}
                  >
                    Upload Excel
                  </Button>
                )}
                <Button
                  type="primary"
                  onClick={() => setIsVisible(true)}
                  className={btnStyles.lgBtn}
                  block={isMobile}
                >
                  <PlusOutlined /> Service
                </Button>
              </Col>
            </Row>
          ) : (
            <Result icon={<Empty />} />
          )}
        </>
      )}
      {isVisible && (
        <ServiceForm
          open={isVisible}
          setOpen={setIsVisible}
          refreshPage={refreshPage}
        />
      )}
      {uploadModalOpen && (
        <BulkUploadModal
          open={uploadModalOpen}
          setOpen={setUploadModalOpen}
          refreshPage={refreshPage}
          title="Bulk Upload Services"
          requiredColumns={[
            "Name",
            "Description",
            "Category",
            "Cash Price",
            "Cash Withtax",
            "Insurance Price",
            "Insurance Withtax",
          ]}
          sampleHint="Tax is optional; leave blank to default to 0."
          sampleFileName="bulk_import_curengo_service_sample.xlsx"
          uploadApi={uploadServicesApi}
          fileField="services"
          prepareUploadFile={prepareServiceBulkUploadFile}
        />
      )}
    </>
  );
};

export default Service;
